/**
 * @file Registry-aware option resolution for parent-board runtime controls.
 */

import { appData } from "../../app/state/state.js";
import {
  getBoardRegistryEntry,
  getBoardRegistryEntryByItemType,
} from "./boardRegistry.js";
import {
    getPatientProviderFilterOptions,
} from "../utils/providerUtils.js";
import {
    getUserFullDisplayName,
    getUsersWithPermission,
} from "../utils/userDisplay.js";

function cloneOption(option) {
  return { ...option };
}

function getConfigList(configKey) {
  return configKey ? appData?.config?.[configKey] || [] : [];
}

function getConfigOptions(configKey) {
  return getConfigList(configKey).map((item) => ({
    value: item.id,
    text: item.name,
    order: Number(item.order ?? 999),
  }));
}

function getUserOptions(collectionName) {
  return getUsersWithPermission(collectionName).map((user) => ({
    value: user.id,
    text: getUserFullDisplayName(user),
  })).sort((a, b) => a.text.localeCompare(b.text));
}

function getStreamOptions(viewKey) {
  if (viewKey === "labs") {
    return [
      { value: "external", text: "External" },
      { value: "internal", text: "In-house" },
    ];
  }
  if (viewKey === "maintenance") {
    return [
      { value: "issue", text: "Issue" },
      { value: "service", text: "Service" },
    ];
  }
  return [];
}

function getLabOptions() {
  const externalLabs = appData?.config?.labs?.externalLabs || [];
  const internalLabLabel =
    appData?.config?.labs?.internalLabLabel || "Internal lab";
  return [
    { value: "internal", text: internalLabLabel },
    ...externalLabs
      .filter((lab) => lab?.isActive !== false)
      .map((lab) => ({
        value: lab.id,
        text: lab.name,
        order: Number(lab.order ?? 999),
      })),
  ];
}

function getMaintenanceTechnicianOptions() {
  const configuredOptions = (appData?.config?.maintenance?.technicians || [])
    .filter((technician) => technician?.isActive !== false)
    .map((technician) => ({
      value: technician.name,
      text: technician.name,
      order: Number(technician.order ?? 999),
    }));
  const configuredNames = new Set(configuredOptions.map((option) => option.value));
  const freeTextOptions = (appData?.maintenance || [])
    .map((item) => String(item?.technician || "").trim())
    .filter(Boolean)
    .filter((name) => !configuredNames.has(name))
    .filter((name, index, names) => names.indexOf(name) === index)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      value: name,
      text: `${name} (Other)`,
      order: 10000,
    }));

  return [...configuredOptions, ...freeTextOptions];
}

/**
 * Returns sort options for a registered parent-board view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {Array<object>} Sort options in display order.
 */
export function getBoardSortOptions(viewKey) {
  return (getBoardRegistryEntry(viewKey)?.runtimeControls?.sortOptions || [])
    .map(cloneOption);
}

/**
 * Returns filter definitions for a registered parent-board view.
 * @param {string} viewKey - Parent-board view key.
 * @returns {Array<object>} Filter definitions in display order.
 */
export function getBoardFilterDefinitions(viewKey) {
  return (getBoardRegistryEntry(viewKey)?.runtimeControls?.filterDefinitions || [])
    .map(cloneOption);
}

/**
 * Returns runtime filter options for a registered parent-board filter.
 * @param {string} viewKey - Parent-board view key.
 * @param {string} filterKey - Filter field key.
 * @returns {Array<object>} Filter options in display order.
 */
export function getBoardFilterOptions(viewKey, filterKey) {
  const board = getBoardRegistryEntry(viewKey);
  const fields = board?.fields || {};

  if (filterKey === "types") {
    return getConfigOptions(fields.typeConfigKey);
  }

  if (filterKey === "priority") {
    return getConfigOptions(fields.priorityConfigKey);
  }

  if (filterKey === fields.statusField) {
    return getConfigOptions(fields.statusConfigKey);
  }

  if (filterKey === "stream") {
    return getStreamOptions(viewKey);
  }

  switch (`${viewKey}:${filterKey}`) {
    case "tasks:owner":
    case "tasks:assigned_users":
      return getUserOptions(null);
    case "tasks:subtask":
      return [
        { value: "yes", text: "Yes" },
        { value: "no", text: "No" },
      ];
    case "patients:case_manager":
      return getUserOptions("patients");
    case "patients:provider":
      return getPatientProviderFilterOptions();
    case "projects:project_manager":
      return getUserOptions("projects");
    case "labs:provider":
      return getPatientProviderFilterOptions();
    case "labs:assigned_users":
      return getUserOptions("labs");
    case "labs:lab":
      return getLabOptions();
    case "maintenance:managed_by":
      return getUserOptions("maintenance");
    case "maintenance:technician":
      return getMaintenanceTechnicianOptions();
    default:
      return [];
  }
}

/**
 * Returns the registry-owned config list for a parent-board item field kind.
 * @param {string} itemType - Singular parent-board item type.
 * @param {"types" | "status" | "priority"} kind - Config field kind.
 * @returns {Array<object>} Runtime config entries.
 */
export function getBoardConfigListForItemType(itemType, kind) {
  const fields = getBoardRegistryEntryByItemType(itemType)?.fields || {};

  if (kind === "types") {
    return getConfigList(fields.typeConfigKey);
  }
  if (kind === "status") {
    return getConfigList(fields.statusConfigKey);
  }
  if (kind === "priority") {
    return getConfigList(fields.priorityConfigKey);
  }
  return [];
}

/**
 * Resolves type IDs to display names using registry-owned config keys.
 * @param {string} itemType - Singular parent-board item type.
 * @param {Array<string>} [typeIds=[]] - Selected type IDs.
 * @returns {Array<string>} Display names or fallback IDs.
 */
export function resolveBoardTypeNames(itemType, typeIds = []) {
  const config = getBoardConfigListForItemType(itemType, "types");
  return (Array.isArray(typeIds) ? typeIds : [])
    .filter(Boolean)
    .map((typeId) => config.find((entry) => entry.id === typeId)?.name || typeId);
}

/**
 * Resolves a board item's status label using registry-owned status metadata.
 * @param {string} itemType - Singular parent-board item type.
 * @param {object} item - Board item.
 * @returns {string} Status label, fallback value, or empty string.
 */
export function resolveBoardStatusLabel(itemType, item) {
  const fields = getBoardRegistryEntryByItemType(itemType)?.fields || {};
  const value = item?.[fields.statusField];
  const config = getBoardConfigListForItemType(itemType, "status");
  return config.find((entry) => entry.id === value)?.name || value || "";
}

/**
 * Resolves a board item's priority label using registry-owned priority metadata.
 * @param {string} itemType - Singular parent-board item type.
 * @param {object} item - Board item.
 * @returns {string} Priority label or empty string.
 */
export function resolveBoardPriorityLabel(itemType, item) {
  const value = item?.priority;
  if (!value) {return "";}
  return getBoardConfigListForItemType(itemType, "priority")
    .find((entry) => entry.id === value)?.name || "";
}
